// How a canvas click changes the selected BPMN element.
//
// Kept in its own module (no bpmn-js imports) so it is unit-testable without
// loading the full modeler that helpers.ts pulls in.

/**
 * How long to wait before applying a same-element deselect.
 *
 * A double-click is two `element.click` events (detail 1, then 2) close
 * together, then `element.dblclick`. Clearing selection on the first of those
 * would close the element drawer before the second click / dblclick can keep
 * it open. Deferring the clear lets the second click cancel it. Long enough
 * to cover a normal double-click gap; short enough that a deliberate
 * single-click close still feels snappy.
 */
export const DESELECT_DEBOUNCE_MS = 250;

/**
 * The selection a click on `clicked` produces, given what is selected now.
 *
 * A single click on the already-selected element clears the selection — in
 * edit mode that toggle is the element drawer's close gesture. The second
 * click of a DOUBLE-click is exempt: diagram-js delivers two element.click
 * events before element.dblclick starts label direct editing, so the toggle
 * would deselect the very element whose label the user is about to type into,
 * taking the drawer down with it.
 *
 * `clickCount` is the native MouseEvent `detail`: 1 for a single click, 2 for
 * the second click of a double-click.
 *
 * Callers must not apply an `undefined` result immediately — schedule it with
 * {@link createDeferredDeselect} so a following double-click click can cancel
 * the clear before the drawer watcher sees an empty selection.
 */
export const selectionAfterClick = <T>(
    selected: T | undefined,
    clicked: T,
    clickCount: number = 1
): T | undefined =>
    clickCount > 1 || selected !== clicked ? clicked : undefined;

/**
 * Schedules clearing selection, with cancel so a double-click's later events
 * can keep the current selection (and the element drawer) intact.
 */
export const createDeferredDeselect = (
    clear: () => void,
    delayMs: number = DESELECT_DEBOUNCE_MS
) => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const cancel = () => {
        if (timer === undefined) return;
        clearTimeout(timer);
        timer = undefined;
    };

    const schedule = () => {
        cancel();
        timer = setTimeout(() => {
            timer = undefined;
            clear();
        }, delayMs);
    };

    return { schedule, cancel };
};
